import type { FC, ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import type { GlassHandle } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Link from 'next/link'

import { GlassDefs } from '@/components/visionaries/glass-defs'
import { bakeGlassMap, supportsNativeGlass } from '@/lib/glass'

import {
    clamp,
    easeInOutExpo,
    power3Out,
    power4Out,
    prefersReducedMotion,
    smoothstep,
    wrapOff
} from '@/lib/motion'

import {
    SCENES,
    VIS,
    sceneSrc,
    toneSepia
} from '@/constants/visionaries'

const N = VIS.N
const STEP_ANGLE = (Math.PI * 2) / N

type Engine = {
    pos: number
    focal: number
    tween: { from: number, to: number, start: number } | null

    W: number
    H: number
    S: number
    phone: boolean
    ringR: number
    ringCY: number
    d0: number

    t0: number
    reduced: boolean
    native: boolean
    inView: boolean
    hovering: boolean

    mapScale: number
    staticSet: boolean
    bgT: number

    dragging: boolean
    startX: number
    startPos: number
    downIndex: number
    vel: number
    lastX: number
    lastT: number

    lastInput: number
    lastAdvance: number

    wheelAcc: number
    wheelTime: number
    wheelMag: number
    wheelLocked: boolean
    wheelPeaked: boolean
}

export const Visionaries: FC = (): ReactNode => {
    const router = useRouter()

    const rootRef = useRef<HTMLElement>(null)
    const bgWrapRef = useRef<HTMLDivElement>(null)
    const glowRef = useRef<HTMLDivElement>(null)
    const glassRef = useRef<GlassHandle>(null)

    const bgRefs = useRef<(HTMLDivElement | null)[]>([])
    const medRefs = useRef<(HTMLDivElement | null)[]>([])
    const imgRefs = useRef<(HTMLImageElement | null)[]>([])
    const rimRefs = useRef<(HTMLDivElement | null)[]>([])

    const [focal, setFocal] = useState<number>(VIS.INITIAL)

    const navRef = useRef<{
        step: (dir: number) => void
        goTo: (i: number) => void
        rotateTo: (target: number) => void
        markInput: () => void
    } | null>(null)

    const eng = useRef<Engine>({
        pos: VIS.INITIAL,
        focal: VIS.INITIAL,
        tween: null,

        W: 0,
        H: 0,
        S: 0,
        phone: false,
        ringR: 0,
        ringCY: 0,
        d0: 0,

        t0: 0,
        reduced: false,
        native: false,
        inView: true,
        hovering: false,

        mapScale: 0,
        staticSet: false,
        bgT: -1,

        dragging: false,
        startX: 0,
        startPos: 0,
        downIndex: -1,
        vel: 0,
        lastX: 0,
        lastT: 0,

        lastInput: 0,
        lastAdvance: 0,

        wheelAcc: 0,
        wheelTime: 0,
        wheelMag: 0,
        wheelLocked: false,
        wheelPeaked: false
    })

    // ------------------------------------------------------------------ engine
    useEffect(() => {
        const root = rootRef.current
        if (!root) return

        const s = eng.current

        s.t0 = performance.now()
        s.reduced = prefersReducedMotion()
        s.native = supportsNativeGlass()

        if (s.native) root.classList.add('vis-native')

        const measure = (): void => {
            const W = root.clientWidth
            const H = root.clientHeight
            const S = Math.min(W, H)
            const phone = S < VIS.PHONE

            s.W = W
            s.H = H
            s.S = S
            s.phone = phone
            s.ringR = S * (phone ? VIS.RING_R_PHONE : VIS.RING_R)
            s.ringCY = H * VIS.RING_CY

            const d0 = Math.round(
                clamp(
                    S * (phone ? VIS.CARD_FACTOR_PHONE : VIS.CARD_FACTOR),
                    VIS.CARD_MIN,
                    VIS.CARD_MAX
                )
            )

            if (d0 === s.d0) return

            s.d0 = d0

            for (const el of medRefs.current) {
                if (!el) continue
                el.style.width = `${d0}px`
                el.style.height = `${d0}px`
            }

            const map = bakeGlassMap(d0, VIS.BEZEL_RATIO, VIS.THICKNESS)

            if (map) {
                s.mapScale = map.scale
                s.staticSet = false
                glassRef.current?.setMap(map.url, map.size)
            }
        }

        measure()

        // --------------------------------------------------------- navigation
        const markInput = (): void => {
            s.lastInput = performance.now()
        }

        const rotateTo = (target: number): void => {
            const now = performance.now()
            s.tween = { from: s.pos, to: target, start: now }
            s.lastAdvance = now
        }

        const step = (dir: number): void => rotateTo(Math.round(s.pos) + dir)
        const goTo = (i: number): void => rotateTo(s.pos + wrapOff(i - s.pos, N))

        // -------------------------------------------------------------- frame
        let raf = 0

        const frame = (now: number): void => {
            raf = requestAnimationFrame(frame)
            if (!s.W) return

            const elapsed = now - s.t0 - VIS.ENTER_DELAY
            const enter = s.reduced ? 1 : power4Out(clamp(elapsed / VIS.ENTER_DUR, 0, 1))
            const bgT = s.reduced ? 1 : power3Out(clamp(elapsed / VIS.ENTER_BG_DUR, 0, 1))

            const enterEased = smoothstep(enter)
            const spin = (1 - enterEased) * VIS.ENTER_SPIN
            const er = 0.5 + 0.5 * enterEased

            if (bgT !== s.bgT) {
                s.bgT = bgT

                const wrap = bgWrapRef.current

                if (wrap) {
                    wrap.style.opacity = bgT.toFixed(3)
                    wrap.style.transform = `scale(${(1.12 - 0.12 * bgT).toFixed(4)})`

                    // drop the filter entirely once it settles: a residual blur(0)
                    // still costs a full-screen pass every frame
                    wrap.style.filter =
                        bgT >= 1 ? 'none' : `blur(${((1 - bgT) * 20).toFixed(2)}px)`
                }

                const glow = glowRef.current
                if (glow) glow.style.opacity = (0.32 * bgT).toFixed(3)
            }

            if (s.tween) {
                const t = clamp((now - s.tween.start) / VIS.STEP_DUR, 0, 1)

                s.pos = s.tween.from + (s.tween.to - s.tween.from) * easeInOutExpo(t)
                if (t >= 1) {
                    s.pos = s.tween.to
                    s.tween = null
                }
            }

            const nextFocal = ((Math.round(s.pos) % N) + N) % N

            if (nextFocal !== s.focal) {
                s.focal = nextFocal
                setFocal(nextFocal)
            }

            for (let i = 0; i < N; i++) {
                const off = wrapOff(i - s.pos, N)
                const abs = Math.abs(off)

                const bg = bgRefs.current[i]
                if (bg) bg.style.opacity = clamp(1.05 - abs, 0, 1).toFixed(3)

                const ang = -Math.PI / 2 + off * STEP_ANGLE + spin
                const bx = Math.cos(ang) * s.ringR * er
                const by = s.ringCY + Math.sin(ang) * s.ringR * er

                const topness = smoothstep(clamp(1 - abs, 0, 1))
                const sc = VIS.BASE_SCALE + VIS.TOP_EMPHASIS * topness
                const lift = VIS.LIFT * topness
                const z = Math.round(100 + topness * 120 - by * 0.02)

                const stg = (clamp(abs, 0, N / 2) / (N / 2)) * 0.5
                const le = smoothstep(clamp((enter - stg) / (1 - stg), 0, 1))
                const eScale = 0.3 + 0.7 * le

                const med = medRefs.current[i]

                if (med) {
                    med.style.transform =
                        `translate(-50%, -50%) translate(${bx.toFixed(2)}px, ${(by - lift).toFixed(2)}px) scale(${(sc * eScale).toFixed(4)})`
                    med.style.opacity = le.toFixed(3)
                    med.style.visibility = le < 0.02 ? 'hidden' : 'visible'
                    med.style.zIndex = String(z)
                }

                const img = imgRefs.current[i]

                if (img) {
                    const gray = clamp((1 - topness) * 0.55, 0, 0.55)
                    const sep = toneSepia(SCENES[i].tone)
                    const bright = 0.9 + 0.1 * topness
                    const blur = (1 - le) * 8

                    img.style.filter =
                        `grayscale(${gray.toFixed(3)}) sepia(${sep}) brightness(${bright.toFixed(3)}) blur(${blur.toFixed(2)}px)`
                }

                const rim = rimRefs.current[i]
                if (rim) rim.style.opacity = (0.42 + 0.4 * topness).toFixed(3)
            }

            if (s.native && s.mapScale > 0) {
                const live = s.inView && !s.reduced

                if (live) {
                    const wob =
                        1 +
                        VIS.WOBBLE *
                            (Math.sin(now * 0.0015) +
                                0.38 * Math.sin(now * 0.0034 + 1.2) +
                                0.22 * Math.sin(now * 0.0061 + 2.4))

                    const base = s.mapScale

                    glassRef.current?.setScales(
                        base * (1 + 0.05 * VIS.CHROMA) * wob,
                        base * wob,
                        base * (1 - 0.05 * VIS.CHROMA) * wob
                    )

                    s.staticSet = false
                } else if (!s.staticSet) {
                    const base = s.mapScale

                    glassRef.current?.setScales(
                        base * (1 + 0.05 * VIS.CHROMA),
                        base,
                        base * (1 - 0.05 * VIS.CHROMA)
                    )

                    s.staticSet = true
                }
            }
        }

        raf = requestAnimationFrame(frame)

        // ------------------------------------------------------------- wheel
        const onWheel = (e: WheelEvent): void => {
            e.preventDefault()

            const dy = e.deltaY + e.deltaX
            const mag = Math.abs(dy)
            const prev = s.wheelMag
            const now = performance.now()

            s.wheelMag = mag

            if (now - s.wheelTime > VIS.WHEEL_GAP) {
                s.wheelAcc = 0
                s.wheelLocked = false
                s.wheelPeaked = false
            }

            s.wheelTime = now

            if (s.wheelLocked) {
                if (mag < prev) s.wheelPeaked = true
                if (!(s.wheelPeaked && mag > prev + 10)) return

                s.wheelLocked = false
                s.wheelPeaked = false
                s.wheelAcc = 0
            }

            s.wheelAcc += dy

            if (Math.abs(s.wheelAcc) >= VIS.WHEEL_THRESHOLD) {
                markInput()
                step(Math.sign(s.wheelAcc))

                s.wheelAcc = 0
                s.wheelLocked = true
                s.wheelPeaked = false
            }
        }

        root.addEventListener('wheel', onWheel, { passive: false })

        // ---------------------------------------------------------- keyboard
        const onKey = (e: KeyboardEvent): void => {
            if (!s.hovering) return

            const dir =
                e.key === 'ArrowRight' || e.key === 'ArrowDown'
                    ? 1
                    : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                      ? -1
                      : 0

            if (!dir) return

            e.preventDefault()
            markInput()
            step(dir)
        }

        window.addEventListener('keydown', onKey)

        // ------------------------------------------------------------ resize
        let resizeTimer = 0

        const ro = new ResizeObserver(() => {
            window.clearTimeout(resizeTimer)
            resizeTimer = window.setTimeout(measure, 120)
        })

        ro.observe(root)

        // ---------------------------------------------------------- in-view
        const io = new IntersectionObserver(
            entries => {
                s.inView = entries[0]?.isIntersecting ?? true
            },
            { threshold: 0.05 }
        )

        io.observe(root)

        // ------------------------------------------------------- auto-rotate
        const tick = window.setInterval(() => {
            if (s.reduced || s.dragging) return

            const now = performance.now()

            if (now - s.t0 - VIS.ENTER_DELAY < VIS.ENTER_DUR) return
            if (!s.inView || document.hidden) return
            if (now - s.lastInput < VIS.USER_IDLE) return
            if (now - s.lastAdvance < VIS.AUTO_INTERVAL) return

            step(1)
        }, VIS.AUTO_TICK)

        // handles used by the pointer + chevron handlers below
        navRef.current = { step, goTo, rotateTo, markInput }

        return () => {
            cancelAnimationFrame(raf)
            window.clearInterval(tick)
            window.clearTimeout(resizeTimer)
            window.removeEventListener('keydown', onKey)
            root.removeEventListener('wheel', onWheel)
            ro.disconnect()
            io.disconnect()
        }
    }, [])

    // ------------------------------------------------------------- pointer
    const onPointerDown = (e: ReactPointerEvent<HTMLElement>): void => {
        if (e.button !== 0) return

        const target = e.target as HTMLElement
        if (target.closest('.vis-chev, .vis-title-link')) return

        const s = eng.current
        const root = rootRef.current

        root?.setPointerCapture(e.pointerId)

        s.dragging = true
        s.tween = null
        s.startX = e.clientX
        s.startPos = s.pos
        s.vel = 0
        s.lastX = e.clientX
        s.lastT = performance.now()

        const disc = target.closest('.vis-med')
        s.downIndex = disc ? Number(disc.getAttribute('data-index')) : -1

        navRef.current?.markInput()
    }

    const onPointerMove = (e: ReactPointerEvent<HTMLElement>): void => {
        const s = eng.current
        if (!s.dragging) return

        const perStep = s.phone ? clamp(s.W * 0.38, 130, 260) : VIS.DRAG_PER_STEP
        const now = performance.now()
        const dt = now - s.lastT

        s.pos = s.startPos - (e.clientX - s.startX) / perStep

        if (dt > 0) {
            const dPos = -(e.clientX - s.lastX) / perStep
            s.vel = s.vel * 0.6 + dPos * (16.67 / dt) * 0.4
        }

        s.lastX = e.clientX
        s.lastT = now

        navRef.current?.markInput()
    }

    const onPointerUp = (e: ReactPointerEvent<HTMLElement>): void => {
        const s = eng.current
        if (!s.dragging) return

        s.dragging = false

        const root = rootRef.current
        if (root?.hasPointerCapture(e.pointerId)) root.releasePointerCapture(e.pointerId)

        const dx = e.clientX - s.startX

        if (Math.abs(dx) <= 4) {
            // a tap on the disc already at the top opens its page; any other
            // disc is brought round to the top first
            const settled = Math.abs(s.pos - Math.round(s.pos)) < 0.08

            if (s.downIndex >= 0 && s.downIndex === s.focal && settled) {
                void router.push(`/violencias/${SCENES[s.downIndex].slug}`)
            } else if (s.downIndex >= 0) {
                navRef.current?.goTo(s.downIndex)
            }
        } else {
            const proj = s.phone
                ? clamp(s.vel * 2.4, -0.5, 0.5)
                : clamp(s.vel * 6, -1.2, 1.2)

            navRef.current?.rotateTo(Math.round(s.pos + proj))
        }

        navRef.current?.markInput()
    }

    const scene = SCENES[focal]

    return (
        <section
            ref={rootRef}
            id='visionaries'
            className='vis-root'
            aria-roledescription="carrossel"
            aria-label="Nove formas de violência"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerEnter={() => { eng.current.hovering = true }}
            onPointerLeave={() => { eng.current.hovering = false }}
        >
            <div ref={bgWrapRef} className='vis-bgwrap' aria-hidden='true'>
                {SCENES.map((item, i) => (
                    <div
                        key={item.image}
                        ref={el => { bgRefs.current[i] = el }}
                        className='vis-bg'
                        style={{
                            backgroundImage: `url(${sceneSrc(item.image)})`,
                            backgroundPosition: item.bgFocus,
                            opacity: i === VIS.INITIAL ? 1 : 0
                        }}
                    />
                ))}
            </div>

            <div className='vis-vignette' aria-hidden='true' />

            <div
                ref={glowRef}
                className='vis-glow'
                aria-hidden='true'
                style={{ opacity: 0 }}
            />

            <GlassDefs ref={glassRef} />

            <div className='vis-orbit'>
                {SCENES.map((item, i) => (
                    <div
                        key={item.image}
                        ref={el => { medRefs.current[i] = el }}
                        className='vis-med'
                        data-index={i}
                    >
                        <div className='vis-med-face'>
                            <img
                                ref={el => { imgRefs.current[i] = el }}
                                className='vis-med-img'
                                src={sceneSrc(item.image)}
                                alt={item.title}
                                draggable={false}
                                style={{
                                    objectPosition: item.thumbFocus,
                                    transform: `scale(${item.thumbScale})`
                                }}
                            />
                        </div>

                        <div
                            ref={el => { rimRefs.current[i] = el }}
                            className='vis-med-rim'
                        />
                    </div>
                ))}
            </div>

            <div className='vis-title'>
                <h2 key={`n-${focal}`} className='vis-title-name'>
                    {scene.title}
                </h2>

                <div key={`c-${focal}`} className='vis-title-sub'>
                    {scene.caption}
                </div>

                <Link
                    key={`l-${focal}`}
                    href={`/violencias/${scene.slug}`}
                    className='vis-title-link'
                >
                    <span>
                        Saiba como agir
                    </span>
                </Link>
            </div>

            <button
                type='button'
                className='vis-chev vis-chev-left'
                aria-label="Forma de violência anterior"
                onClick={() => {
                    navRef.current?.markInput()
                    navRef.current?.step(-1)
                }}
            >
                <ChevronLeft size={22} strokeWidth={1.6} aria-hidden='true' />
            </button>

            <button
                type='button'
                className='vis-chev vis-chev-right'
                aria-label="Próxima forma de violência"
                onClick={() => {
                    navRef.current?.markInput()
                    navRef.current?.step(1)
                }}
            >
                <ChevronRight size={22} strokeWidth={1.6} aria-hidden='true' />
            </button>
        </section>
    )
}
